(function (root, factory) {
  if (typeof module === 'object' && module.exports) {
    module.exports = factory(
      require('./ai-chat-state.js'),
      require('./ai-chat-markdown.js'),
      null,
    );
    return;
  }

  if (root) {
    root.MarineMaxAIChatMessages = factory(
      root.MarineMaxAIChatState,
      root.MarineMaxAIChatMarkdown,
      root,
    );
  }
})(typeof window !== 'undefined' ? window : null, function (state, markdown, root) {
  'use strict';

  if (!state) {
    throw new Error('MarineMax AI Chat state is required by the messages module');
  }
  if (!markdown) {
    throw new Error('MarineMax AI Chat Markdown is required by the messages module');
  }

  const { messageFormats, messageTypes } = state;

  function createMessageController(options = {}) {
    const container = options.container;
    const documentRef =
      options.document ||
      (container && container.ownerDocument) ||
      (root && root.document);
    const store = options.store || state.createMessageStore();
    const renderMarkdown =
      options.renderMarkdown ||
      ((content) => markdown.renderMarkdown(content, { document: documentRef }));

    if (!container) {
      throw new Error('A container is required by the AI Chat messages module');
    }
    if (!documentRef || typeof documentRef.createElement !== 'function') {
      throw new Error('A document is required by the AI Chat messages module');
    }

    function createElement(message) {
      const element = documentRef.createElement('div');
      element.dataset.aiChatMessageId = String(message.id);

      if (message.type === messageTypes.TYPING) {
        element.className = 'ai-typing-indicator';
        element.setAttribute('role', 'status');
        element.setAttribute('aria-label', 'Assistant is typing');
        element.innerHTML = [
          '<span class="ai-typing-dot" aria-hidden="true"></span>',
          '<span class="ai-typing-dot" aria-hidden="true"></span>',
          '<span class="ai-typing-dot" aria-hidden="true"></span>',
        ].join('');
        return element;
      }

      element.className =
        message.type === messageTypes.USER ? 'user-message' : 'ai-message';

      if (
        message.format === messageFormats.MARKDOWN &&
        message.type !== messageTypes.ERROR
      ) {
        element.innerHTML = renderMarkdown(message.content);
      } else {
        element.textContent = message.content;
      }

      return element;
    }

    function getElement(messageId) {
      return container.querySelector(
        `[data-ai-chat-message-id="${messageId}"]`,
      );
    }

    function scrollToBottom() {
      container.scrollTop = container.scrollHeight;
    }

    function append(type, content = '', format = messageFormats.MARKDOWN) {
      const message = store.add(type, content, format);
      container.appendChild(createElement(message));
      scrollToBottom();
      return message;
    }

    function update(messageId, content) {
      const message = store.update(messageId, content);
      if (!message) return null;

      const currentElement = getElement(messageId);
      if (currentElement) {
        currentElement.replaceWith(createElement(message));
        scrollToBottom();
      }
      return message;
    }

    function remove(messageId) {
      const removed = store.remove(messageId);
      const element = getElement(messageId);
      if (element) element.remove();
      return removed;
    }

    function render() {
      container.replaceChildren(
        ...store.snapshot().map((message) => createElement(message)),
      );
      scrollToBottom();
    }

    function clear() {
      store.clear();
      container.replaceChildren();
      container.scrollTop = 0;
    }

    return Object.freeze({
      append,
      clear,
      getElement,
      remove,
      render,
      store,
      update,
    });
  }

  return Object.freeze({
    createMessageController,
  });
});
